import { z } from 'zod';

const moneyDto = z.object({
  amount: z.number(),
  currency: z.enum(['GEL', 'USD', 'EUR']),
});

const imageDto = z.object({
  url: z.string().url(),
  alt: z.string().default(''),
  width: z.number().int().positive().optional(),
  height: z.number().int().positive().optional(),
});

const priceBreakdownDto = z.object({
  subtotal: moneyDto,
  discount: moneyDto,
  shipping: moneyDto,
  tax: moneyDto,
  total: moneyDto,
});

const promotionKindDto = z.discriminatedUnion('type', [
  z.object({ type: z.literal('percent-off'), percent: z.number().min(0).max(100) }),
  z.object({ type: z.literal('amount-off'), amount: moneyDto }),
  z.object({ type: z.literal('free-shipping') }),
  z.object({ type: z.literal('gift'), description: z.string() }),
]);

const promotionDto = z.object({
  id: z.union([z.string(), z.number()]).transform(String),
  code: z.string().nullable().default(null),
  label: z.string(),
  kind: promotionKindDto,
  starts_at: z.string().nullable().default(null),
  ends_at: z.string().nullable().default(null),
});

const appliedPromotionDto = z.object({
  promotion: promotionDto,
  discount_amount: moneyDto,
});

const cartItemCharacteristicDto = z.object({
  name: z.string(),
  value: z.string(),
});

const cartItemDto = z.object({
  product_id: z.union([z.string(), z.number()]).transform(String),
  sku: z.union([z.string(), z.number()]).transform(String),
  slug: z.string().nullable().default(null),
  title: z.string(),
  image: imageDto.nullable().default(null),
  characteristics: z.array(cartItemCharacteristicDto).default([]),
  unit_price: moneyDto,
  compare_at_price: moneyDto.nullable().default(null),
  quantity: z.number().int().min(0),
  line_total: moneyDto,
  available_quantity: z.number().int().min(0).nullable().default(null),
});

export const cartDto = z.object({
  id: z.union([z.string(), z.number()]).transform(String),
  items: z.array(cartItemDto).default([]),
  item_count: z.number().int().min(0).default(0),
  currency: z.enum(['GEL', 'USD', 'EUR']),
  totals: priceBreakdownDto,
  promotions: z.array(appliedPromotionDto).default([]),
  updated_at: z.string(),
});

export type CartDto = z.infer<typeof cartDto>;
export type CartItemDto = z.infer<typeof cartItemDto>;
export type AppliedPromotionDto = z.infer<typeof appliedPromotionDto>;
export type PromotionDto = z.infer<typeof promotionDto>;
